import React, { useState, useEffect, useCallback } from 'react';
import { FiMail, FiUsers, FiSend, FiCheck, FiX, FiAlertCircle, FiSettings, FiEye, FiEyeOff, FiRefreshCw, FiCheckCircle, FiXCircle, FiSearch } from 'react-icons/fi';
import { getUsersForBulkEmail, sendBulkEmail, getActiveCampaign, getRecipientStatuses, resetCampaign, getUserIdsByStatus, getEmailConfig } from '../services/adminMailService';
import Spinner from '../components/Spinner';

const AdminMail = () => {
  // State for user management
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [lastUserId, setLastUserId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [pageHistory, setPageHistory] = useState([]); // Track pagination history

  // Filtering state
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [emailSearchInput, setEmailSearchInput] = useState(''); // Input field value
  const [emailSearch, setEmailSearch] = useState(''); // Actual search term applied

  // Email composition state
  const [emailSubject, setEmailSubject] = useState('Important Update from Bela');
  const [showPreview, setShowPreview] = useState(false);
  const [editMode, setEditMode] = useState('form'); // 'form' or 'html'
  
  // Form fields for email content
  const [salutation, setSalutation] = useState('Hello from Bela! 💕');
  const [mainMessage, setMainMessage] = useState('I hope this message finds you well! I wanted to reach out and share something special with you.');
  const [closing, setClosing] = useState('Thank you for being part of The Bela Experience! 💖');
  
  // HTML editor state (used when in HTML mode)
  const [customHtml, setCustomHtml] = useState('');

  // Bulk email state
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [sendProgress, setSendProgress] = useState(null);

  // Test mode state
  const [emailConfig, setEmailConfig] = useState({ isTestMode: false, testRecipient: null });

  // Campaign state
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [recipientStatuses, setRecipientStatuses] = useState({}); // Map of userId -> {status, email, sentAt, error}
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loadingCampaign, setLoadingCampaign] = useState(false);

  const usersPerPage = 50;

  // Generate HTML from form fields
  const generateEmailHtml = () => {
    return `<div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; color: #2c3e50;">
  <h2 style="color: #2c3e50; margin-bottom: 20px; font-weight: 600;">${salutation}</h2>

  <p style="font-size: 16px; line-height: 1.6; color: #5a5a5a; margin-bottom: 25px;">
    ${mainMessage}
  </p>

  <div style="text-align: center; margin: 25px 0;">
    <a href="https://thebelaexperience.com" style="display: inline-block; background-color: #F25278; color: white; padding: 14px 32px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px;">
      Visit The Bela Experience
    </a>
  </div>

  <p style="color: #6c757d; font-size: 14px; line-height: 1.5; margin-top: 35px; text-align: center;">
    ${closing}
  </p>
</div>`;
  };

  // Get the email HTML based on current mode
  const getEmailHtml = () => {
    if (editMode === 'form') {
      return generateEmailHtml();
    }
    return customHtml;
  };

  // Initialize custom HTML when switching to HTML mode
  useEffect(() => {
    if (editMode === 'html' && !customHtml) {
      setCustomHtml(generateEmailHtml());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, customHtml]);

  // Load active campaign on mount
  useEffect(() => {
    loadActiveCampaign();
  }, []);

  // Load active campaign
  const loadActiveCampaign = async () => {
    try {
      setLoadingCampaign(true);
      const campaignData = await getActiveCampaign();
      setActiveCampaign(campaignData.campaign);
      
      // Load campaign content if exists
      if (campaignData.campaign && campaignData.campaign.subject) {
        setEmailSubject(campaignData.campaign.subject);
      }
      if (campaignData.campaign && campaignData.campaign.htmlBody) {
        setCustomHtml(campaignData.campaign.htmlBody);
      }
    } catch (err) {
      console.error('Error loading campaign:', err);
      setError('Failed to load campaign');
    } finally {
      setLoadingCampaign(false);
    }
  };

  // Load recipient statuses when users change
  useEffect(() => {
    if (users.length > 0 && activeCampaign) {
      loadRecipientStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, activeCampaign]);

  // Load recipient statuses for current users
  const loadRecipientStatuses = async () => {
    if (!users.length || !activeCampaign) return;

    try {
      const userIds = users.map(u => u.uid);
      const statusData = await getRecipientStatuses(userIds);
      setRecipientStatuses(statusData.statusMap);
    } catch (err) {
      console.error('Error loading recipient statuses:', err);
    }
  };

  // Handle reset campaign
  const handleResetCampaign = async () => {
    if (!window.confirm('Are you sure you want to reset the campaign? This will mark the current campaign as complete and create a new one. All send statuses will be reset.')) {
      return;
    }

    try {
      setLoadingCampaign(true);
      const newCampaign = await resetCampaign();
      setActiveCampaign(newCampaign.campaign);
      setRecipientStatuses({});
      setEmailResult(null);
      
      // Reset to default content
      setEmailSubject('Important Update from Bela');
      setSalutation('Hello from Bela! 💕');
      setMainMessage('I hope this message finds you well! I wanted to reach out and share something special with you.');
      setClosing('Thank you for being part of The Bela Experience! 💖');
      setCustomHtml('');
      setEditMode('form');
      
      alert('Campaign reset successfully!');
    } catch (err) {
      console.error('Error resetting campaign:', err);
      setError('Failed to reset campaign');
    } finally {
      setLoadingCampaign(false);
    }
  };

  // Select ALL unsent users (across all pages), excluding opted-out
  const handleSelectAllUnsent = async () => {
    try {
      setLoading(true);
      const unsentUserIds = await getUserIdsByStatus('unsent');
      
      // Filter out opted-out users
      const optedOutIds = new Set(users.filter(u => u.emailNotificationsEnabled === false).map(u => u.uid));
      const selectableIds = unsentUserIds.filter(id => !optedOutIds.has(id));
      
      // Select ALL unsent users (not opted out), not just current page
      setSelectedUserIds(new Set(selectableIds));
      
      console.log(`Selected ${selectableIds.length} unsent users across all pages (${unsentUserIds.length - selectableIds.length} opted out skipped)`);
    } catch (err) {
      console.error('Error selecting unsent users:', err);
      setError('Failed to select unsent users');
    } finally {
      setLoading(false);
    }
  };

  // Select ALL failed users (across all pages), excluding opted-out
  const handleSelectAllFailed = async () => {
    try {
      setLoading(true);
      const failedUserIds = await getUserIdsByStatus('fail');
      
      // Filter out opted-out users
      const optedOutIds = new Set(users.filter(u => u.emailNotificationsEnabled === false).map(u => u.uid));
      const selectableIds = failedUserIds.filter(id => !optedOutIds.has(id));
      
      // Select ALL failed users (not opted out), not just current page
      setSelectedUserIds(new Set(selectableIds));
      
      console.log(`Selected ${selectableIds.length} failed users across all pages (${failedUserIds.length - selectableIds.length} opted out skipped)`);
    } catch (err) {
      console.error('Error selecting failed users:', err);
      setError('Failed to select failed users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users with current filters and pagination
  const fetchUsers = useCallback(async (cursor = null, filter = subscriptionFilter, search = emailSearch) => {
    setLoadingUsers(true);
    setError(null);

    try {
      const result = await getUsersForBulkEmail({
        lastUserId: cursor,
        limit: usersPerPage,
        subscriptionFilter: filter,
        emailSearch: search || null
      });

      setUsers(result.users || []);
      setHasMore(result.pagination?.hasMore || false);
      setLastUserId(result.pagination?.nextCursor || null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, [subscriptionFilter]);

  // Fetch email config on mount
  useEffect(() => {
    const fetchEmailConfig = async () => {
      try {
        const config = await getEmailConfig();
        setEmailConfig(config);
      } catch (err) {
        console.error('Error fetching email config:', err);
      }
    };
    fetchEmailConfig();
  }, []);

  // Initial load
  useEffect(() => {
    fetchUsers(null, subscriptionFilter, emailSearch);
    setPageHistory([]); // Reset history on filter change
  }, [subscriptionFilter, emailSearch]);

  // Handle user selection (skip opted-out users)
  const handleUserSelect = (userId) => {
    const user = users.find(u => u.uid === userId);
    if (user?.emailNotificationsEnabled === false) return; // Can't select opted-out users
    
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Handle select all users on current page (exclude opted-out)
  const handleSelectAll = () => {
    const selectableUsers = users.filter(user => user.emailNotificationsEnabled !== false);
    const allSelected = selectableUsers.every(user => selectedUserIds.has(user.uid));
    if (allSelected) {
      // Deselect all on current page
      setSelectedUserIds(prev => {
        const newSet = new Set(prev);
        users.forEach(user => newSet.delete(user.uid));
        return newSet;
      });
    } else {
      // Select all on current page (only those not opted out)
      setSelectedUserIds(prev => {
        const newSet = new Set(prev);
        selectableUsers.forEach(user => newSet.add(user.uid));
        return newSet;
      });
    }
  };

  // Handle bulk email sending
  // Show confirmation modal before sending
  const handleShowConfirmModal = () => {
    if (selectedUserIds.size === 0) {
      setError('Please select at least one user to send email to.');
      return;
    }

    if (!emailSubject.trim()) {
      setError('Please enter an email subject.');
      return;
    }

    const emailHtml = getEmailHtml();
    if (!emailHtml.trim()) {
      setError('Please enter email content.');
      return;
    }

    if (!activeCampaign) {
      setError('No active campaign found. Please reload the page.');
      return;
    }

    setShowConfirmModal(true);
  };

  // Actually send the emails with polling progress
  const handleSendBulkEmail = async () => {
    setShowConfirmModal(false);
    setSendingEmail(true);
    setEmailResult(null);
    setSendProgress({ sent: 0, total: selectedUserIds.size, success: 0, failed: 0 });
    setError(null);

    const userIdsArray = Array.from(selectedUserIds);
    let pollingInterval = null;
    let pollCount = 0;
    const maxPolls = 60; // 2 minutes max (2s * 60)

    const pollProgress = async () => {
      try {
        const statusData = await getRecipientStatuses(userIdsArray);
        const statusMap = statusData.statusMap || {};
        
        let success = 0;
        let failed = 0;
        let pending = 0;
        
        userIdsArray.forEach(uid => {
          const status = statusMap[uid]?.status;
          if (status === 'success') success++;
          else if (status === 'fail') failed++;
          else pending++;
        });
        
        const sent = success + failed;
        setSendProgress({ sent, total: userIdsArray.length, success, failed, pending });
        
        // Stop polling if all done
        if (sent >= userIdsArray.length || pollCount >= maxPolls) {
          if (pollingInterval) {
            clearInterval(pollingInterval);
            pollingInterval = null;
          }
        }
        
        return { sent, total: userIdsArray.length, complete: sent >= userIdsArray.length };
      } catch (err) {
        console.error('Error polling progress:', err);
        return { sent: 0, total: userIdsArray.length, complete: false };
      }
    };

    try {
      const emailHtml = getEmailHtml();
      
      // Start polling immediately
      pollingInterval = setInterval(() => {
        pollCount++;
        pollProgress();
      }, 2000);
      
      // Initial poll
      await pollProgress();
      
      const result = await sendBulkEmail({
        campaignId: activeCampaign.id,
        userIds: userIdsArray,
        subject: emailSubject,
        htmlBody: emailHtml
      });

      setEmailResult(result);

      // Final poll to get complete status
      await pollProgress();
      
      // Keep polling briefly to catch any final updates
      await new Promise(resolve => setTimeout(resolve, 3000));
      await pollProgress();
      
      // Stop polling
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }

      // Reload recipient statuses to show updated send status
      await loadRecipientStatuses();

      // Clear selection on success
      if (result.success) {
        setSelectedUserIds(new Set());
      }
    } catch (err) {
      setError(err.message);
      console.error('Error sending bulk email:', err);
    } finally {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      setSendingEmail(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setSubscriptionFilter(newFilter);
    setLastUserId(null);
    setPageHistory([]);
    // Keep selections when filter changes (selections are independent of filter)
  };

  // Handle email search
  const handleEmailSearch = () => {
    if (emailSearchInput.trim()) {
      setEmailSearch(emailSearchInput.trim());
      setLastUserId(null);
      setPageHistory([]);
    } else {
      // Clear search if input is empty
      setEmailSearch('');
      setLastUserId(null);
      setPageHistory([]);
    }
  };

  // Handle search input key press (Enter key)
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleEmailSearch();
    }
  };

  // Handle pagination - Next page
  const handleNextPage = () => {
    if (hasMore && lastUserId) {
      // Save current state to history before moving forward
      setPageHistory(prev => [...prev, { cursor: lastUserId, users: [...users] }]);
      fetchUsers(lastUserId, subscriptionFilter);
      // Keep selections across pages for cross-page selection
    }
  };

  // Handle pagination - Previous page
  const handlePrevPage = () => {
    if (pageHistory.length > 0) {
      const newHistory = [...pageHistory];
      const previousPage = newHistory.pop();
      setPageHistory(newHistory);
      
      // Restore previous page state
      setUsers(previousPage.users);
      setLastUserId(previousPage.cursor);
      setHasMore(true); // We know there's at least one more page (current one)
      // Keep selections across pages for cross-page selection
    }
  };

  // Get subscription status display
  const getSubscriptionStatus = (user) => {
    if (!user.subscriptionStatus) return 'Unknown';

    switch (user.subscriptionStatus) {
      case 'trial':
        return 'Trial';
      case 'active':
        return 'Active';
      case 'expired':
        return 'Expired';
      default:
        return user.subscriptionStatus;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'trial':
        return 'text-blue-600 bg-blue-100';
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'expired':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <FiMail className="mr-3 text-blue-600" />
            Admin Bulk Email
          </h1>
          <p className="mt-2 text-gray-600">
            Send bulk emails to selected users with advanced filtering and preview options.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <FiAlertCircle className="text-red-500 mr-3 flex-shrink-0" />
            <span className="text-red-700">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <FiX size={20} />
            </button>
          </div>
        )}

        {/* Success Result */}
        {emailResult && (
          <div className={`mb-6 border rounded-lg p-4 flex items-start ${
            emailResult.success
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            {emailResult.success ? (
              <FiCheck className="text-green-500 mr-3 flex-shrink-0 mt-0.5" />
            ) : (
              <FiAlertCircle className="text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <h3 className={`font-semibold ${
                emailResult.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {emailResult.success ? 'Email Sent Successfully' : 'Email Send Failed'}
              </h3>
              <p className={`mt-1 ${
                emailResult.success ? 'text-green-700' : 'text-red-700'
              }`}>
                {emailResult.message}
              </p>
              {emailResult.stats && (
                <div className="mt-2 text-sm">
                  <span className="font-medium">Total: {emailResult.stats.total}</span>
                  <span className="mx-2">•</span>
                  <span className="text-green-600">Sent: {emailResult.stats.sent}</span>
                  <span className="mx-2">•</span>
                  <span className="text-red-600">Failed: {emailResult.stats.failed}</span>
                  {emailResult.stats.duration && (
                    <>
                      <span className="mx-2">•</span>
                      <span>Duration: {emailResult.stats.duration}s</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setEmailResult(null)}
              className={`ml-4 ${
                emailResult.success ? 'text-green-500 hover:text-green-700' : 'text-red-500 hover:text-red-700'
              }`}
            >
              <FiX size={20} />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* User Selection Panel */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <FiUsers className="mr-2 text-blue-600" />
                  User Selection
                </h2>
                <span className="text-sm text-gray-500">
                  {selectedUserIds.size} selected
                </span>
              </div>

              {/* Email Search */}
              <div className="mt-4 mb-2">
                <div className="flex gap-2 max-w-md">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Search by email (starts with)..."
                      value={emailSearchInput}
                      onChange={(e) => setEmailSearchInput(e.target.value)}
                      onKeyPress={handleSearchKeyPress}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <button
                    onClick={handleEmailSearch}
                    disabled={loadingUsers}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <FiSearch />
                    Search
                  </button>
                  {emailSearch && (
                    <button
                      onClick={() => {
                        setEmailSearchInput('');
                        setEmailSearch('');
                        setLastUserId(null);
                        setPageHistory([]);
                      }}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {emailSearch && (
                  <p className="text-xs text-gray-600 mt-1">
                    Searching for emails starting with: <strong>{emailSearch}</strong>
                  </p>
                )}
              </div>

              {/* Filters */}
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'All Users' },
                  { value: 'trial', label: 'Trial Users' },
                  { value: 'active', label: 'Active Users' },
                  { value: 'expired', label: 'Expired Users' }
                ].map(filter => (
                  <button
                    key={filter.value}
                    onClick={() => handleFilterChange(filter.value)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      subscriptionFilter === filter.value
                        ? 'bg-blue-100 text-blue-800 border border-blue-300'
                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {/* User List */}
            <div className="p-6">
              {/* Selection Summary */}
              {selectedUserIds.size > 0 && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">
                    <FiUsers className="inline mr-2" />
                    {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''} selected
                    {selectedUserIds.size > users.length && (
                      <span className="text-blue-700"> (includes users from other pages)</span>
                    )}
                  </p>
                </div>
              )}

              {/* Selection Action Buttons */}
              <div className="mb-4 flex flex-wrap gap-2">
                <button
                  onClick={handleSelectAll}
                  disabled={loadingUsers}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md font-medium disabled:opacity-50"
                >
                  {users.every(user => selectedUserIds.has(user.uid)) ? 'Deselect' : 'Select'} (This Page Only)
                </button>
                <button
                  onClick={handleSelectAllUnsent}
                  disabled={loading || loadingUsers}
                  className="px-3 py-1 text-sm bg-green-100 text-green-700 hover:bg-green-200 rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Select All Unsent'}
                </button>
                <button
                  onClick={handleSelectAllFailed}
                  disabled={loading || loadingUsers}
                  className="px-3 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Select All Failed'}
                </button>
                {selectedUserIds.size > 0 && (
                  <button
                    onClick={() => setSelectedUserIds(new Set())}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md font-medium"
                  >
                    Clear All Selections
                  </button>
                )}
                <button
                  onClick={handleResetCampaign}
                  disabled={loadingCampaign}
                  className="ml-auto px-3 py-1 text-sm bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded-md font-medium flex items-center disabled:opacity-50"
                >
                  <FiRefreshCw className="mr-1" />
                  {loadingCampaign ? 'Resetting...' : 'Reset Campaign'}
                </button>
              </div>

              {/* User List */}
              <div className="relative">
                {loadingUsers && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10 rounded-lg">
                    <Spinner />
                  </div>
                )}
                {users.length === 0 && !loadingUsers ? (
                  <div className="text-center py-8 text-gray-500">
                    No users found
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {users.map(user => {
                      const sendStatus = recipientStatuses[user.uid]?.status || 'unsent';
                      const isOptedOut = user.emailNotificationsEnabled === false;
                      
                      return (
                        <div
                          key={user.uid}
                          className={`flex items-center p-3 rounded-lg border transition-colors ${
                            isOptedOut
                              ? 'bg-gray-100 border-gray-200 opacity-60'
                              : selectedUserIds.has(user.uid)
                                ? 'bg-blue-50 border-blue-300 cursor-pointer'
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100 cursor-pointer'
                          }`}
                          onClick={() => !isOptedOut && handleUserSelect(user.uid)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.has(user.uid)}
                            onChange={() => handleUserSelect(user.uid)}
                            disabled={isOptedOut}
                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {user.displayName || 'No name'}
                                </p>
                                {/* Opted Out Badge */}
                                {isOptedOut && (
                                  <span className="inline-flex items-center text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                                    Opted out
                                  </span>
                                )}
                                {/* Send Status Indicator */}
                                {sendStatus === 'success' && (
                                  <span className="inline-flex items-center text-xs text-green-600">
                                    <FiCheckCircle className="mr-1" /> Sent
                                  </span>
                                )}
                                {sendStatus === 'fail' && (
                                  <span className="inline-flex items-center text-xs text-red-600">
                                    <FiXCircle className="mr-1" /> Failed
                                  </span>
                                )}
                              </div>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.subscriptionStatus)}`}>
                                {getSubscriptionStatus(user)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 truncate">{user.email}</p>
                            <p className="text-xs text-gray-400">
                              Joined: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {(hasMore || pageHistory.length > 0) && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {users.length} users
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={pageHistory.length === 0 || loadingUsers}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={!hasMore || loadingUsers}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Email Composition Panel */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <FiSend className="mr-2 text-blue-600" />
                  Email Composition
                </h2>
                <div className="flex items-center space-x-2">
                  {/* Edit Mode Toggle */}
                  <div className="flex border border-gray-300 rounded-md overflow-hidden">
                    <button
                      onClick={() => setEditMode('form')}
                      className={`px-3 py-1 text-sm ${
                        editMode === 'form'
                          ? 'bg-blue-100 text-blue-800 font-medium'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Form Editor
                    </button>
                    <button
                      onClick={() => setEditMode('html')}
                      className={`px-3 py-1 text-sm border-l border-gray-300 ${
                        editMode === 'html'
                          ? 'bg-blue-100 text-blue-800 font-medium'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      HTML
                    </button>
                  </div>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {showPreview ? <FiEyeOff className="mr-1" /> : <FiEye className="mr-1" />}
                    {showPreview ? 'Hide' : 'Preview'}
                  </button>
                </div>
              </div>

              {/* Email Mode Indicator */}
              <div className={`mt-3 p-3 rounded-md ${
                emailConfig.isTestMode 
                  ? 'bg-yellow-50 border border-yellow-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-center">
                  {emailConfig.isTestMode ? (
                    <>
                      <FiAlertCircle className="text-yellow-600 mr-2" />
                      <span className="text-sm text-yellow-800 font-medium">Test Mode Enabled</span>
                    </>
                  ) : (
                    <>
                      <FiCheckCircle className="text-green-600 mr-2" />
                      <span className="text-sm text-green-800 font-medium">Live Mode</span>
                    </>
                  )}
                </div>
                <p className="text-xs mt-1 ${emailConfig.isTestMode ? 'text-yellow-700' : 'text-green-700'}">
                  {emailConfig.isTestMode 
                    ? `All emails will be sent to test recipient: ${emailConfig.testRecipient}`
                    : 'Emails will be sent to actual user email addresses'
                  }
                </p>
              </div>
            </div>

            <div className="p-6">
              {/* Email Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email subject..."
                  />
                </div>

                {/* Form Editor */}
                {editMode === 'form' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Salutation
                      </label>
                      <input
                        type="text"
                        value={salutation}
                        onChange={(e) => setSalutation(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Hello from Bela! 💕"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Main Message
                      </label>
                      <textarea
                        value={mainMessage}
                        onChange={(e) => setMainMessage(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Your message to recipients..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Closing
                      </label>
                      <input
                        type="text"
                        value={closing}
                        onChange={(e) => setClosing(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Thank you for being part of The Bela Experience! 💖"
                      />
                    </div>
                  </div>
                )}

                {/* HTML Editor */}
                {editMode === 'html' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      HTML Content *
                    </label>
                    <textarea
                      value={customHtml}
                      onChange={(e) => setCustomHtml(e.target.value)}
                      rows={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      placeholder="<div><h1>Hello!</h1><p>This is your email content...</p></div>"
                    />
                  </div>
                )}

                {/* Preview */}
                {showPreview && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preview
                    </label>
                    <div className="border border-gray-300 rounded-md p-4 max-h-64 overflow-y-auto bg-white">
                      <div dangerouslySetInnerHTML={{ __html: getEmailHtml() }} />
                    </div>
                  </div>
                )}

                {/* Send Button */}
                <div className="pt-4">
                  <button
                    onClick={handleShowConfirmModal}
                    disabled={sendingEmail || selectedUserIds.size === 0}
                    className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendingEmail ? (
                      <>
                        <Spinner className="mr-2" />
                        {sendProgress ? (
                          <span>
                            Sending {sendProgress.sent} of {sendProgress.total}
                            {sendProgress.success > 0 && ` (${sendProgress.success} success)`}
                            {sendProgress.failed > 0 && ` (${sendProgress.failed} failed)`}
                          </span>
                        ) : (
                          'Sending Email...'
                        )}
                      </>
                    ) : (
                      <>
                        <FiSend className="mr-2" />
                        Send to {selectedUserIds.size} User{selectedUserIds.size !== 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Bulk Email Send</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Recipients:</span>
                        <span className="ml-2 text-gray-900">{selectedUserIds.size} users</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Subject:</span>
                        <span className="ml-2 text-gray-900">{emailSubject}</span>
                      </div>
                      {emailConfig.isTestMode && (
                        <div className="col-span-2">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <FiAlertCircle className="mr-1" />
                            Test Mode: Sending to {emailConfig.testRecipient}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Preview:</label>
                    <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto bg-gray-50">
                      <div dangerouslySetInnerHTML={{ __html: getEmailHtml() }} />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendBulkEmail}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium flex items-center justify-center"
                  >
                    <FiSend className="mr-2" />
                    Confirm & Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMail;